import React, { useCallback, useEffect, useMemo, useState } from 'react';
import s from './s.module.less';
import { Button, Form, Input, Popconfirm, Typography, message, Modal } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import CommonTable from 'components/CommonTable';
import debounce from 'lodash/debounce';
import { useRequest } from 'ahooks';
import { deleteDevWhiteListMembers, getDevWhitelist, updateDevWhiteLis, addDevWhitelistMember } from 'api/access';
import { IWhitelistRowData } from 'types/access';
import dayjs from 'dayjs';
import AccessRoleStore from 'store/Access/role';
import { checkUserRole, transferRoleStringToNunber } from 'utils/access';
import { limitedEmails } from 'constants/access';
import remove from 'lodash/remove';

interface IRow extends IWhitelistRowData {
    editable: boolean;
    dataIndex: string;
    title: string;
}

interface EditableCellProps extends React.HTMLAttributes<HTMLElement> {
    editing: boolean;
    dataIndex: string;
    title: string;
    inputType: string;
    record: IWhitelistRowData;
    index: number;
    options: Array<{ label: string, value: number }>,
    children: React.ReactNode;
}

const emailTailErrorText = 'Only can add members with @helloklarity.com @kiwihealth.com @boxpractice.com @hush.com email';

const EditableCell = ({
    options,
    editing,
    dataIndex,
    title,
    inputType,
    record,
    index,
    children,
    ...restProps
}: EditableCellProps) => {
    const inputNode = <Input />;
    const rules: Record<string, any>[] = [
        {
            required: true,
        },
    ];

    if (dataIndex === 'email') {
        rules.push(
            {
                validator: (r, value: string) => {
                    if (!limitedEmails.includes(value?.split('@')[1])) {
                        return Promise.reject(
                            new Error(emailTailErrorText),
                        );
                    }

                    return Promise.resolve(true);
                },
            },
        );
    }
    return (
        <td {...restProps}>
            {editing ? (
                <Form.Item
                    name={dataIndex}
                    style={{ margin: 0 }}
                    help=""
                    rules={rules}
                >
                    {inputNode}
                </Form.Item>
            ) : (
                children
            )}
        </td>
    );
};

const DevWhitelist = () => {
    const [getAccessRoleStore] = AccessRoleStore.useStore();
    const roleStore = getAccessRoleStore('data');
    const [modal, contextHolder] = Modal.useModal();
    const [formInstance] = Form.useForm();
    const [filterParams, setFilterParams] = useState({
        firstName: '',
        lastName: '',
        email: '',
    });
    const [selectedRows, setSelectedRows] = useState<Array<IWhitelistRowData>>([]);
    const [editingKey, setEditingKey] = useState<number | undefined>(-1);
    const [tableData, setTableData] = useState<Array<IWhitelistRowData>>([]);
    const [originalTableData, setOriginalTableData] = useState<Array<IWhitelistRowData>>([]);

    const { data, loading, run } = useRequest(getDevWhitelist);
    const userRole = checkUserRole(roleStore);

    useEffect(() => {
        const originalData = data?.data?.data || [];

        setTableData(originalData);
        setOriginalTableData(originalData);
    }, [data]);

    const tableIsEditing = useCallback(() => editingKey !== -1, [editingKey]);

    const filterNameDebounce = useCallback(debounce((params: Partial<IWhitelistRowData>) => {
        const filterSourceData = originalTableData;

        const filterRes = filterSourceData.filter((item:IWhitelistRowData) => {
            const res = Object.entries(params).reduce((r, c) => {
                const [key, value] = c as [keyof IWhitelistRowData, string];
                return r && item[key]?.toString().indexOf(value) !== -1;
            }, true);

            return res;
        });

        setTableData(filterRes);
    }, 500), [originalTableData]);

    const handleAddMember = useCallback(() => {
        if (tableIsEditing()) {
            message.error('Please save your changes before adding new data.');
        } else if (tableData[0] && !tableData[0].id) {
            message.error('You must save the previously added member before you add another one');
            setEditingKey(undefined);
            setTimeout(() => {
                formInstance.validateFields();
            }, 100);
        } else {
            formInstance.resetFields();
            const newRowData: IWhitelistRowData = {
                accessToken: '',
                firstName: '',
                email: '',
                lastName: '',
                password: '',
                role: '',
                tel: '',
            };

            setTableData([newRowData].concat(tableData));
            setOriginalTableData([newRowData].concat(originalTableData));
            setEditingKey(undefined);
        }
    }, [formInstance, originalTableData, tableData, tableIsEditing]);

    const handleEdit = useCallback((item: IWhitelistRowData) => {
        if (!tableIsEditing()) {
            formInstance.setFieldsValue(item);
            setEditingKey(item?.id);
        } else {
            message.error('Please save your changes before proceeding to edit another section');
        }
    }, [formInstance, tableIsEditing]);

    const submit = useCallback(async (item: IWhitelistRowData) => {
        try {
            const row = (await formInstance.validateFields()) as IWhitelistRowData;
            const { firstName, lastName, email, role } = row;

            if (limitedEmails.includes(email.split('@')[1])) {
                const { id } = item;
                let res = {
                    error: '',
                };
                const newData = {
                    accessToken: item.accessToken,
                    firstName,
                    email,
                    lastName,
                    password: item.password,
                    tel: item.tel,
                    roleList: [role],
                };

                if (id) {
                    res = await updateDevWhiteLis(id, newData);
                } else {
                    res = await addDevWhitelistMember(newData);
                }

                if (!res.error) {
                    setEditingKey(-1);
                    run();
                } else {
                    message.error(res.error);
                }
            } else {
                message.error(emailTailErrorText);
            }
        } catch (errInfo) {
            if (errInfo?.errorFields?.find((field) => field?.name?.[0] === 'email')) {
                message.error(emailTailErrorText);
            }
            console.log('Validate Failed:', errInfo);
        }
    }, [formInstance, run]);

    const handleCancelEdit = useCallback(() => {
        setEditingKey(-1);
        remove(tableData, (item) => !item.id);
        setTableData([...tableData]);
    }, [tableData]);

    const confirmDelete = useCallback(async () => {
        const ids = selectedRows.filter((e) => !!e.id).map((e) => e.id) as number[];
        const res = await deleteDevWhiteListMembers(ids);
        // const res = await deleteOpa1sMemberById(ids[0]);

        if (!res.error) {
            run();
        } else {
            message.error(res.error);
        }
    }, [run, selectedRows]);

    const handleDeleteMembers = useCallback(() => {
        if (selectedRows.length > 0) {
            modal.confirm({
                title: 'Confirm Revoke Access',
                content: 'do you want to revoke access to development environment for these users?',
                onOk: confirmDelete,
                okText: 'Delete',
            });
        }
    }, [selectedRows.length, modal, confirmDelete]);

    const isEditingRow = useCallback((record: IRow) => {
        return record.id === editingKey;
    }, [editingKey]);

    const renderActionColunm = useCallback((item) => {
        if (userRole === 0) {
            const editable = isEditingRow(item);

            return (
                <div className={s.actionCell}>
                    {
                        editable ? (
                            <span>
                                <Typography.Link onClick={() => submit(item)} className={s.actionBtn}>
                                    Save
                                </Typography.Link>
                                <Popconfirm title="Are you sure you want to cancel?" placement="topRight" onConfirm={handleCancelEdit}>
                                    <a className={s.cancelBtn}>Cancel</a>
                                </Popconfirm>
                            </span>
                        ) : (
                            <Typography.Link className={s.actionBtn} onClick={() => handleEdit(item)}>
                                Edit
                            </Typography.Link>
                        )
                    }
                </div>
            );
        } else if (userRole === 1) {
            const editable = isEditingRow(item);
            const role = transferRoleStringToNunber(item.role);

            return userRole < role ? (
                <div className={s.actionCell}>
                    {
                        editable ? (
                            <span>
                                <Typography.Link onClick={() => submit(item)} className={s.actionBtn}>
                                    Save
                                </Typography.Link>
                                <Popconfirm title="Confirm to cancel?" placement="topRight" onConfirm={handleCancelEdit}>
                                    <a className={s.cancelBtn}>Cancel</a>
                                </Popconfirm>
                            </span>
                        ) : (
                            <Typography.Link className={s.actionBtn} onClick={() => handleEdit(item)}>
                                Edit
                            </Typography.Link>
                        )
                    }
                </div>
            ) : null;
        }

        return null;
    }, [handleCancelEdit, handleEdit, isEditingRow, submit, userRole]);

    const columns: ColumnsType<IRow> = useMemo(() => (
        [
            {
                title: 'FIRST NAME',
                dataIndex: 'firstName',
                filterSearch: true,
                className: s.tableColumn,
                filterIcon: <SearchOutlined />,
                editable: true,
                filterDropdown: () => (
                    <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                        <Input
                            onChange={(e) => {
                                const firstName = e.target.value;
                                setTimeout(() => {
                                    setFilterParams({
                                        ...filterParams,
                                        firstName,
                                    });

                                    filterNameDebounce({
                                        ...filterParams,
                                        firstName,
                                    });
                                }, 0);
                            }}
                            style={{ marginBottom: 8, display: 'block' }}
                        />
                    </div>
                ),
            },
            {
                title: 'LAST NAME',
                dataIndex: 'lastName',
                filterSearch: true,
                className: s.tableColumn,
                filterIcon: <SearchOutlined />,
                editable: true,
                filterDropdown: () => (
                    <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                        <Input
                            onChange={(e) => {
                                const lastName = e.target.value;
                                setFilterParams({
                                    ...filterParams,
                                    lastName,
                                });
                                filterNameDebounce({
                                    ...filterParams,
                                    lastName,
                                });
                            }}
                            style={{ marginBottom: 8, display: 'block' }}
                        />
                    </div>
                ),
            },
            {
                title: 'CONTACT',
                dataIndex: 'email',
                width: '300px',
                className: s.tableColumn,
                filterIcon: <SearchOutlined />,
                editable: true,
                filterDropdown: () => (
                    <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                        <Input
                            onChange={(e) => {
                                const email = e.target.value;
                                setFilterParams({
                                    ...filterParams,
                                    email,
                                });
                                filterNameDebounce({
                                    ...filterParams,
                                    email,
                                });
                            }}
                            style={{ marginBottom: 8, display: 'block' }}
                        />
                    </div>
                ),
            },
            {
                title: 'LAST MODIFY',
                dataIndex: 'updateTime',
                width: '200px',
                className: s.tableColumn,
                render: (updateTime) => updateTime && dayjs(updateTime).format('YYYY-MM-DD hh:mm:ss'),
            },
            {
                title: '',
                width: '120px',
                className: s.tableColumn,
                editable: false,
                render: renderActionColunm,
            },
        ]
    ), [filterNameDebounce, filterParams, renderActionColunm]);

    const mergedColumns = columns.map((col) => {
        if (!col.editable) {
            return col;
        }
        return {
            ...col,
            onCell: (record: IRow) => {
                return ({
                    record,
                    inputType: 'text',
                    dataIndex: col.dataIndex,
                    title: col.title,
                    editing: isEditingRow(record),
                });
            },
        };
    });

    return (
        <div className={s.wrap}>
            {contextHolder}
            <div className={s.title}>Dev whitelist</div>
            <div className={s.inviteBar}>
                {
                    userRole <= 1 &&
                    <div className={s.deleteBar}>
                        <div className={s.deleteBtn} onClick={handleDeleteMembers}>Delete</div>
                        <div className={s.selectedNumbers}>Selected {selectedRows.length} member(s)</div>
                    </div>
                }
                { userRole <= 1 && <Button type="primary" onClick={handleAddMember}>Invite a new member</Button> }
            </div>
            <div className={s.tableWrap}>
                <Form form={formInstance} component={false}>
                    <CommonTable
                        pagination={false}
                        components={{
                            body: {
                                cell: EditableCell,
                            },
                        }}
                        rowSelection={{
                            onChange: (selectedRowKeys, selectedRowsData) => setSelectedRows(selectedRowsData),
                        }}
                        rowKey={(record: IWhitelistRowData) => record.id || record.accessToken}
                        bordered
                        loading={loading}
                        columns={mergedColumns}
                        data={tableData}
                    />
                </Form>
            </div>
        </div>
    );
};

export default DevWhitelist;
